import { Criteria } from '../../data/criteria';
import { FinderContainerComponent } from './finder-container/finder-container.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import {
    MatDialog,
    MatDialogConfig,
    MatDialogRef,
} from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { CommonDialogSettings } from './common-dialog-settings.interface';
import { AlertDialogComponent } from './alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { ProgressDialogComponent } from './progress-dialog/progress-dialog.component';
import { ComponentType } from '@angular/cdk/overlay';
import { Catalog } from 'idk/data/catalog';
import { NotificationDialogComponent } from './notification-dialog/notification-dialog.component';
import {
    DefaultDialogLayoutComponent,
    DefaultDialogLayoutSettings,
} from './default-dialog-layout/default-dialog-layout.component';
import { MultiModeComponent } from '../components/multi-mode.component';
import { IBusinessObject } from 'idk/abstractions/business-object';

@Injectable({
    providedIn: 'root',
})
export class CommonDialogsService {
    public constructor(public dialog: MatDialog) {}

    public error(
        message: string,
        callback?: () => void,
        title: string = ''
    ): void {
        const settings: CommonDialogSettings = {
            body: message,
            title: title,
            icon: 'highlight_off',
            onAceptar: callback,
        };

        this.open(ErrorDialogComponent, {
            width: '600px',
            disableClose: true,
            data: settings,
        });
    }

    public notification(
        title: string = '',
        message: string,
        type: 'success' | 'warn' | 'error' | 'info',
        callback?: (result: boolean) => void
    ): void {
        const settings: CommonDialogSettings = {
            body: message,
            title: title,
            icon: 'info',
            type: type,
            onAceptar: callback,
        };

        this.open(NotificationDialogComponent, {
            width: '600px',
            disableClose: true,
            panelClass: 'notification-panel-dialog',
            data: settings,
        });
    }

    public alert(
        message: string,
        callback?: (result: boolean) => void,
        title: string = '',
        width: string = '600px',
        height: string = 'auto'
    ): void {
        const settings: CommonDialogSettings = {
            body: message,
            title: title,
            icon: 'info',
            onAceptar: callback,
        };

        this.open(AlertDialogComponent, {
            width: width,
            height: height,
            disableClose: true,
            data: settings,
        });
    }

    public confirm(
        message: string,
        callback?: (result: boolean) => void,
        title: string = '',
        yes: string = 'Aceptar',
        no: string = 'Cancelar'
    ): void {
        const settings: CommonDialogSettings = {
            body: message,
            title: title,
            icon: 'help_outline',
            onAceptar: callback,
            onCancelar: callback,
            button1Title: yes,
            button2Title: no,
        };

        this.open(ConfirmDialogComponent, {
            width: '600px',
            disableClose: true,
            data: settings,
        });
    }

    public progress(
        message: string = 'Estamos procesando su solicitud, por favor espere...'
    ): MatDialogRef<ProgressDialogComponent> {
        const settings: CommonDialogSettings = {
            body:
                message ??
                'Estamos procesando su solicitud, por favor espere...',
            icon: 'sync',
        };

        const dialog: MatDialogRef<ProgressDialogComponent> = this.open(
            ProgressDialogComponent,
            {
                width: '600px',
                data: settings,
                disableClose: true,
            }
        );

        return dialog;
    }

    public screenBlocking(): MatDialogRef<ProgressDialogComponent> {
        const settings: CommonDialogSettings = {
            body: '',
            icon: 'sync',
        };

        const dialog: MatDialogRef<ProgressDialogComponent> = this.open(
            ProgressDialogComponent,
            {
                width: '600px',
                data: settings,
                disableClose: true,
                panelClass: 'panel-no-visible',
            }
        );

        return dialog;
    }

    private _forceClosing<T, R = any>(dialog: MatDialogRef<T, R>): void {
        dialog.beforeClosed().subscribe({
            next: r => {
                (dialog as any)._finishDialogClose();
                document.getElementById(dialog.id)?.closest('.cdk-global-overlay-wrapper')?.remove();
            }
        });
    }

    public open<T, D = any, R = any>(
        component: ComponentType<T>,
        config?: MatDialogConfig<D>
    ): MatDialogRef<T, R> {
        const dlg: MatDialogRef<T, R> =  this.dialog.open(component, {
            ...config,
            exitAnimationDuration: 75,
        });

        this._forceClosing(dlg);

        return dlg;
    }

    public openWithDefaultLayout<T, D = any, R = any>(
        config: MatDialogConfig<DefaultDialogLayoutSettings<T, D>>
    ): MatDialogRef<DefaultDialogLayoutComponent, R> {
        return this.open(DefaultDialogLayoutComponent, config);
    }

    public findOne<
        TComponent extends MultiModeComponent<
            TCatalog,
            TEntity,
            TKey,
            TCriteria
        >,
        TCatalog extends Catalog<TEntity, TKey, TCriteria>,
        TEntity extends IBusinessObject<TKey>,
        TKey,
        TCriteria extends Criteria
    >(
        container: ComponentType<
            FinderContainerComponent<
                TComponent,
                TCatalog,
                TEntity,
                TKey,
                TCriteria
            >
        >,
        component: TComponent,
        data: any
    ): Promise<TEntity> {
        const dialog: MatDialogRef<
            FinderContainerComponent<
                TComponent,
                TCatalog,
                TEntity,
                TKey,
                TCriteria
            >,
            TEntity
        > = this.open(container, {
            disableClose: true,
            height: '95%',
            width: '100%',
            data: {
                component,
                data,
            },
        });

        return new Promise<TEntity>((resolve) => {
            dialog.afterClosed().subscribe({
                next: (result) => {
                    resolve(result);
                },
            });
        });
    }

    public findMany<
        TComponent extends MultiModeComponent<
            TCatalog,
            TEntity,
            TKey,
            TCriteria
        >,
        TCatalog extends Catalog<TEntity, TKey, TCriteria>,
        TEntity extends IBusinessObject<TKey>,
        TKey,
        TCriteria extends Criteria
    >(
        container: ComponentType<
            FinderContainerComponent<
                TComponent,
                TCatalog,
                TEntity,
                TKey,
                TCriteria
            >
        >,
        component: TComponent,
        data: any
    ): Promise<TEntity[]> {
        const dialog: MatDialogRef<
            FinderContainerComponent<
                TComponent,
                TCatalog,
                TEntity,
                TKey,
                TCriteria
            >,
            TEntity[]
        > = this.open(container, {
            disableClose: true,
            height: '95%',
            width: '100%',
            data: {
                component,
                data,
            },
        });

        return new Promise<TEntity[]>((resolve) => {
            dialog.afterClosed().subscribe({
                next: (result) => {
                    resolve(result);
                },
            });
        });
    }
}
